'use client'

import { useState } from 'react'
import { Download, Copy, Check, Loader2 } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import { FeatureCard } from '@/components/tools/FeatureCard'
import { ToolEntryHero } from '@/components/tools/ToolEntryHero'
import { Button } from '@/components/ui/button'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

interface GeneratorResult {
  url: string
  domain: string
  generatedAt: string
  llmsTxt: string
  extracted: {
    orgName: string
    hasTagline: boolean
    hasDescription: boolean
    productCount: number
    topicCount: number
    keyUrlCount: number
    hasSitemap: boolean
    hasContact: boolean
    aiAssisted: boolean
  }
}

// Build the "Before you publish" checklist from what the generator could and
// could not detect. These are the gaps a hosted llms.txt should not ship with,
// so they live in the UI, never inside the generated file.
function publishChecklist(e: GeneratorResult['extracted']): string[] {
  const items: string[] = []
  if (!e.hasContact) {
    items.push(
      'Add your contact email on the Contact line (we left a placeholder).'
    )
  }
  if (e.aiAssisted && e.productCount > 0) {
    items.push(
      `Review the ${e.productCount} product description${e.productCount === 1 ? '' : 's'} we drafted from your pages for accuracy and tone.`
    )
  }
  if (e.aiAssisted && e.hasDescription) {
    items.push(
      'Confirm the company overview matches how you position yourself.'
    )
  }
  items.push(
    'Add details we cannot read from your pages: founding year, headquarters, pricing model, and key differentiators.'
  )
  if (e.keyUrlCount < 5) {
    items.push(
      'Add any other important pages to Key URLs (docs, security, customers).'
    )
  }
  if (!e.hasSitemap) {
    items.push('Publish a sitemap at /sitemap.xml and add it to Key URLs.')
  }
  return items
}

const BUILDS = [
  {
    title: 'Identity and overview',
    desc: 'Your name, a one-line summary, and a company overview pulled from your title, meta description, and Organization schema.',
    color: '#008dcc',
  },
  {
    title: 'Products and services',
    desc: 'Detected from Product, Service, and SoftwareApplication schema on your page, with names, descriptions, and URLs where present.',
    color: '#8b5cf6',
  },
  {
    title: 'Key URLs',
    desc: 'Your most important pages (About, Products, Pricing, Blog, Contact) found in your navigation, plus your sitemap if you have one.',
    color: '#F90B8A',
  },
  {
    title: 'Use policy',
    desc: 'A clear attribution and citation policy so AI engines know how they may reference your content.',
    color: '#FDC304',
  },
  {
    title: 'Radar-standard structure',
    desc: 'The same section layout Pixelmojo uses on its own llms.txt, the structure that earns clean AI citations.',
    color: '#22c55e',
  },
  {
    title: 'Bracketed gaps to fill',
    desc: 'Anything we cannot read from your site is left as a clear placeholder, so you know exactly what to complete before publishing.',
    color: '#06b6d4',
  },
]

const RELATED = [
  {
    title: 'llms.txt Validator',
    href: '/tools/llms-txt-validator',
    desc: 'Already have a file? Score it against the spec.',
    color: '#008dcc',
  },
  {
    title: 'AI Crawl Checker',
    href: '/tools/ai-crawl-checker',
    desc: 'Confirm GPTBot and friends can reach your site.',
    color: '#8b5cf6',
  },
  {
    title: 'robots.txt Analyzer',
    href: '/tools/robots-txt-analyzer',
    desc: 'Check which AI bots your robots.txt allows.',
    color: '#F90B8A',
  },
  {
    title: 'AI Readiness Score',
    href: '/tools/ai-readiness-score',
    desc: 'See your overall AI-search readiness in one score.',
    color: '#FDC304',
  },
]

export default function LlmsTxtGeneratorPage() {
  const toolUsage = useToolUsage('llms-txt-generator')
  const gate = useToolAccessGate('llms-txt-generator', 'llms.txt Generator')
  const [result, setResult] = useState<GeneratorResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Input validation lives in ToolEntryHero; this receives the trimmed URL.
  async function handleSubmit(trimmed: string) {
    if (!gate.ready) {
      gate.openGate(trimmed)
      return
    }
    setIsLoading(true)
    setError(null)
    setResult(null)
    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/llms-txt-generator',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )
      const data = await response.json()
      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed)
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }
      if (!response.ok) {
        throw new Error(data.message || 'Generation failed. Please try again.')
      }
      setResult(data as GeneratorResult)
      gate.markConsumed()
      setTimeout(() => {
        document
          .getElementById('generator-result')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  function copyToClipboard() {
    if (!result) return
    navigator.clipboard.writeText(result.llmsTxt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function download() {
    if (!result) return
    const blob = new Blob([result.llmsTxt], { type: 'text/plain' })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = 'llms.txt'
    a.click()
    URL.revokeObjectURL(href)
  }

  return (
    <div className='animate-fade-in'>
      {gate.modal}
      <div className='container mx-auto px-4 py-16 pb-24'>
        {/* Hero */}
        <LenisFadeIn>
          <div className='text-center mb-10'>
            <ToolEntryHero
              title='Generate a Radar-Standard llms.txt in Seconds'
              valueProp='Paste your URL. We read your site and build a structured llms.txt shaped to the same standard Pixelmojo uses on its own site.'
              trustLine='Free · nothing stored · ready in seconds'
              inputLabel='Domain to generate llms.txt for'
              placeholder='yourdomain.com'
              buttonLabel='Generate llms.txt'
              exampleValue='pixelmojo.io'
              isLoading={isLoading}
              onSubmit={handleSubmit}
            />
            <p className='text-sm text-muted-foreground max-w-2xl mx-auto mt-8'>
              llms.txt is an emerging standard that tells AI models what your
              site is, what you offer, and how your content may be cited.
              Hosting a clean one at yourdomain.com/llms.txt improves your
              chances of being accurately represented by ChatGPT, Claude,
              Perplexity, and Gemini. This generator reads your existing page
              signals (title, meta description, structured data, navigation) and
              reads your page content to assemble a structured llms.txt. The
              wording is AI-assisted but grounded only in what is on your pages,
              never fabricated.
            </p>

            {error && (
              <p className='text-sm text-[#F90B8A] mt-4 bg-[#F90B8A]/10 border border-[#F90B8A]/20 rounded-lg px-4 py-3 max-w-xl mx-auto'>
                {error}
              </p>
            )}
          </div>
        </LenisFadeIn>

        {/* Result */}
        <div id='generator-result' className='max-w-3xl mx-auto'>
          {isLoading && (
            <div className='flex flex-col items-center gap-4 py-16 text-muted-foreground'>
              <Loader2 className='w-8 h-8 animate-spin text-primary' />
              <p className='text-sm'>
                Reading your site and building your llms.txt...
              </p>
            </div>
          )}

          {toolUsage.isGated && !isLoading && (
            <UsageLimitGate
              toolName='llms.txt Generator'
              used={toolUsage.used}
              limit={toolUsage.limit}
            />
          )}

          {result && !isLoading && !toolUsage.isGated && (
            <>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-6 md:p-8'>
                <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
                  <div>
                    <p className='text-sm font-semibold text-foreground leading-none'>
                      llms.txt for {result.domain}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1.5'>
                      {result.extracted.productCount} product
                      {result.extracted.productCount === 1 ? '' : 's'},{' '}
                      {result.extracted.keyUrlCount} key URL
                      {result.extracted.keyUrlCount === 1 ? '' : 's'} detected.
                      Fill any [bracketed] gaps before publishing.
                    </p>
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={copyToClipboard}
                    >
                      {copied ? (
                        <Check className='w-4 h-4' />
                      ) : (
                        <Copy className='w-4 h-4' />
                      )}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button
                      type='button'
                      variant='default'
                      size='sm'
                      onClick={download}
                    >
                      <Download className='w-4 h-4' />
                      Download
                    </Button>
                  </div>
                </div>
                <pre className='bg-background border border-line-strong rounded-xl p-4 text-xs md:text-sm text-foreground/90 overflow-x-auto max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words font-mono'>
                  {result.llmsTxt}
                </pre>
                <p className='text-xs text-muted-foreground mt-3'>
                  Host this at{' '}
                  <span className='font-mono text-foreground/80'>
                    {result.url}/llms.txt
                  </span>
                  , then validate it with the llms.txt Validator.
                </p>
              </div>
              <div className='bg-muted/50 rounded-2xl border border-line-strong p-6 md:p-8 mt-4'>
                <p className='text-sm font-semibold text-foreground leading-none'>
                  Before you publish this file
                </p>
                <p className='text-xs text-muted-foreground mt-1.5 mb-4'>
                  We built this from what your pages expose. Finish these so AI
                  engines get the full, accurate picture. None of it is in the
                  file yet, so add it before hosting.
                </p>
                <ul className='space-y-2.5'>
                  {publishChecklist(result.extracted).map((item, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/90'
                    >
                      <span
                        className='mt-1.5 inline-block w-1.5 h-1.5 shrink-0 bg-[#008dcc]'
                        aria-hidden='true'
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        {/* What it builds (pre-result) */}
        {!result && !isLoading && (
          <LenisFadeIn>
            <div className='max-w-4xl mx-auto mt-16'>
              <h2 className='text-2xl md:text-3xl font-heading text-center mb-8'>
                What it builds into your llms.txt
              </h2>
              <div className='grid sm:grid-cols-2 gap-4'>
                {BUILDS.map(b => (
                  <FeatureCard
                    key={b.title}
                    title={b.title}
                    desc={b.desc}
                    color={b.color}
                  />
                ))}
              </div>
            </div>
          </LenisFadeIn>
        )}

        {/* Related tools */}
        <LenisFadeIn>
          <div className='max-w-4xl mx-auto mt-16'>
            <h2 className='text-2xl md:text-3xl font-heading text-center mb-8'>
              Related free tools
            </h2>
            <div className='grid sm:grid-cols-2 gap-4'>
              {RELATED.map(t => (
                <a
                  key={t.href}
                  href={t.href}
                  className='block bg-background border border-line-strong rounded-xl p-4 hover:border-primary transition-colors'
                >
                  <span
                    className='inline-block text-xs font-semibold uppercase tracking-wider mb-1.5'
                    style={{ color: t.color }}
                  >
                    {t.title}
                  </span>
                  <p className='text-sm text-muted-foreground'>{t.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </LenisFadeIn>
      </div>
    </div>
  )
}
